import { motion } from "framer-motion";

function PricingSection() {
  const plans = [
    {
      name: "Free",
      price: "0",
      description: "Great for getting started",
      features: [
        "Process up to 100 emails/month with AI",
        "Basic categorization (limited categories)",
        "Weekly email summary",
        "Basic bulk actions (archive, delete)",
        "Limited search capabilities",
        "Standard customer support",
      ],
      highlighted: false,
      buttonText: "Sign Up Free",
    },
    {
      name: "Pro",
      price: "9.99",
      description: "Perfect for professionals",
      features: [
        "Unlimited emails processed with AI",
        "Advanced categorization (unlimited custom categories)",
        "Daily email summaries and insights",
        "Advanced bulk actions with custom rules",
        "Natural language search across all emails",
        "Priority customer support",
        "Scheduled automatic cleanups",
        "Email templates and response suggestions",
        "Attachment management and organization",
        "Analytics dashboard",
      ],
      highlighted: true,
      buttonText: "Start 14-Day Trial",
    },
  ];

  return (
    <section id="pricing" className="py-20 px-4 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-b from-white to-[#f9f6f4]">
        {/* Grid Pattern */}
        <svg
          className="absolute inset-0 w-full h-full opacity-[0.15]"
          width="100%"
          height="100%"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <pattern
              id="grid"
              width="40"
              height="40"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M0 10 L40 10"
                stroke="#946e5d"
                strokeWidth="0.5"
                fill="none"
              />
              <path
                d="M0 20 L40 20"
                stroke="#946e5d"
                strokeWidth="0.5"
                fill="none"
              />
              <path
                d="M0 30 L40 30"
                stroke="#946e5d"
                strokeWidth="0.5"
                fill="none"
              />
              <path
                d="M10 0 L10 40"
                stroke="#946e5d"
                strokeWidth="0.5"
                fill="none"
              />
              <path
                d="M20 0 L20 40"
                stroke="#946e5d"
                strokeWidth="0.5"
                fill="none"
              />
              <path
                d="M30 0 L30 40"
                stroke="#946e5d"
                strokeWidth="0.5"
                fill="none"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>

        {/* Blob Shapes */}
        <div className="absolute -left-64 -top-64 w-[500px] h-[500px] rounded-full bg-[#ff8c42]/10 blur-3xl"></div>
        <div className="absolute -right-64 top-1/3 w-[400px] h-[400px] rounded-full bg-[#946e5d]/10 blur-3xl"></div>
        <div className="absolute left-1/4 bottom-0 w-[300px] h-[300px] rounded-full bg-[#ff8c42]/5 blur-2xl"></div>
      </div>

      <div className="container mx-auto max-w-6xl relative z-10">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-block mb-4">
            <div className="relative">
              <span className="flex h-3 w-3 absolute -left-3 -top-1">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#ff8c42] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-[#ff8c42]"></span>
              </span>
              <span className="uppercase tracking-wider text-sm font-semibold bg-gradient-to-r from-[#946e5d] to-[#ff8c42] bg-clip-text text-transparent">
                Choose Your Plan
              </span>
            </div>
          </div>

          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-[#694e42]">
            Simple, Transparent Pricing
          </h2>
          <p className="text-lg text-[#8a7266] max-w-2xl mx-auto">
            Choose the plan that fits your needs. No hidden fees or complicated
            tiers.
          </p>
        </motion.div>

        <div className="flex flex-col md:flex-row gap-12 justify-center mx-auto max-w-4xl">
          {plans.map((plan, index) => (
            <motion.div
              key={index}
              className={`
                rounded-xl p-8 flex-1 relative
                ${
                  plan.highlighted
                    ? "bg-white border-2 border-[#ff8c42] shadow-[0_10px_50px_rgba(255,140,66,0.2)]"
                    : "bg-white border border-[#e2dad3] shadow-xl"
                }
              `}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              whileHover={{
                y: -15,
                boxShadow: plan.highlighted
                  ? "0 30px 60px rgba(255, 140, 66, 0.25)"
                  : "0 30px 60px rgba(0, 0, 0, 0.15)",
                transition: { duration: 0.3 },
              }}
            >
              {/* Decorative corner */}
              {/* Decorative corner - new improved version */}
              {plan.highlighted && (
                <div className="absolute top-4 right-4 bg-[#ff8c42] text-white text-xs font-bold py-1 px-3 rounded-full">
                  POPULAR
                </div>
              )}

              {/* Plan icon */}
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 mx-auto
                ${plan.highlighted ? "bg-[#fff5eb]" : "bg-[#f9f6f4]"}`}
              >
                {plan.name === "Free" && (
                  <svg
                    className="w-6 h-6 text-[#946e5d]"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9 12L11 14L15 10M12 22C6.477 22 2 17.523 2 12C2 6.477 6.477 2 12 2C17.523 2 22 6.477 22 12C22 17.523 17.523 22 12 22Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
                {plan.name === "Pro" && (
                  <svg
                    className="w-6 h-6 text-[#ff8c42]"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 15V17M12 7V13M12 22C6.477 22 2 17.523 2 12C2 6.477 6.477 2 12 2C17.523 2 22 6.477 22 12C22 17.523 17.523 22 12 22Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </div>

              <h3 className="text-xl font-bold text-center text-[#694e42]">
                {plan.name}
              </h3>

              <div className="relative text-center mt-4 mb-6">
                <span className="text-5xl font-bold bg-gradient-to-br from-[#694e42] to-[#946e5d] bg-clip-text text-transparent">
                  ${plan.price}
                </span>
                <span className="text-[#8a7266]">/month per user</span>

                {/* Price underline */}
                <motion.div
                  className={`absolute -bottom-2 left-1/2 transform -translate-x-1/2 h-1 rounded-full
                    ${plan.highlighted ? "bg-[#ff8c42]" : "bg-[#e2dad3]"}`}
                  initial={{ width: 0 }}
                  whileInView={{ width: "30%" }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: 0.6 + index * 0.1 }}
                />
              </div>

              <p className="text-center text-[#8a7266] mb-8">
                {plan.description}
              </p>

              <motion.button
                className={`w-full mb-8 py-3 rounded-md font-medium ${
                  plan.highlighted
                    ? "bg-[#ff8c42] hover:bg-[#ff7a29] text-white"
                    : "bg-[#f9f6f4] border border-[#694e42] text-[#694e42] hover:bg-[#f1ece8]"
                }`}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                {plan.buttonText}
              </motion.button>

              <div className="space-y-4">
                <div className="text-sm font-medium text-[#694e42] uppercase tracking-wider mb-3">
                  {plan.highlighted ? "Everything you need:" : "Includes:"}
                </div>

                <ul className="space-y-4">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start">
                      <div
                        className={`mr-3 ${
                          plan.highlighted ? "text-[#ff8c42]" : "text-[#946e5d]"
                        } pt-0.5`}
                      >
                        <svg
                          className="w-5 h-5"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M20 6L9 17L4 12"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <span className="text-[#694e42] text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Decorative pattern */}
              <div className="absolute bottom-0 left-0 right-0 h-12 overflow-hidden opacity-20">
                <svg
                  className="w-full h-full"
                  viewBox="0 0 100 40"
                  xmlns="http://www.w3.org/2000/svg"
                  preserveAspectRatio="none"
                >
                  <path
                    d="M0 40 C 20 0, 50 40, 100 0 L 100 40 L 0 40 Z"
                    fill={plan.highlighted ? "#ff8c42" : "#946e5d"}
                  />
                </svg>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="text-center mt-16 relative"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          {/* Money-back guarantee badge */}
          <div className="absolute left-1/2 top-0 transform -translate-x-1/2 -translate-y-8 bg-white rounded-full border border-[#e2dad3] px-4 py-2 shadow-md flex items-center">
            <svg
              className="w-5 h-5 text-[#ff8c42] mr-2"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className="text-sm font-medium text-[#694e42]">
              14-day money-back guarantee
            </span>
          </div>

          <p className="text-[#8a7266] pt-6">
            Need help choosing?{" "}
            <a
              href="#contact"
              className="text-[#ff8c42] font-medium hover:underline"
            >
              Contact our support team
            </a>
          </p>

          {/* FAQ teaser */}
          <div className="mt-12 p-6 bg-white rounded-xl border border-[#e2dad3] shadow-md max-w-2xl mx-auto">
            <h3 className="text-lg font-bold text-[#694e42] mb-4">
              Frequently Asked Questions
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
              <div className="p-3 hover:bg-[#f9f6f4] rounded-lg transition-colors">
                <h4 className="font-medium text-[#694e42]">
                  Can I change plans later?
                </h4>
                <p className="text-sm text-[#8a7266]">
                  Yes, upgrade or downgrade anytime.
                </p>
              </div>
              <div className="p-3 hover:bg-[#f9f6f4] rounded-lg transition-colors">
                <h4 className="font-medium text-[#694e42]">
                  Is my data secure?
                </h4>
                <p className="text-sm text-[#8a7266]">
                  We use bank-level encryption and never store emails.
                </p>
              </div>
            </div>
            <motion.button
              className="mt-4 text-[#ff8c42] text-sm font-medium"
              whileHover={{ x: 5 }}
              transition={{ type: "spring", stiffness: 400 }}
            >
              View all FAQs →
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default PricingSection;
